import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import SocialIcons from "../components/app/SocialIcons";
import {
  HeartHandshake, Users, Sparkles, MapPin, Tent, MessageCircle, Check
} from "lucide-react";

export default function About() {
  const { t } = useTranslation();
  const promises = [
    [t("about.promise_safe_t"), t("about.promise_safe_b")],
    [t("about.promise_sponsored_t"), t("about.promise_sponsored_b")],
    [t("about.promise_free_t"), t("about.promise_free_b")],
    [t("about.promise_real_t"), t("about.promise_real_b")],
    [t("about.promise_everyone_t"), t("about.promise_everyone_b")],
  ];
  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="about-page">
      <Header active="about" />

      <section className="border-b hairline bg-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="eyebrow mb-4">{t("about.eyebrow")}</div>
          <h1 className="font-display text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-8">
            {t("about.h1_a")}<br/>
            <span className="text-[#ff4500]">{t("about.h1_b")}</span><br/>
            {t("about.h1_c")}
          </h1>
          <p className="text-[19px] leading-relaxed text-[#222] mb-6">
            {t("about.p1")}
          </p>
          <p className="text-[17px] leading-relaxed text-[#333]">
            {t("about.p2_pre")}{" "}
            <strong>{t("about.p2_ask")}</strong>, <strong>{t("about.p2_share")}</strong>, <strong>{t("about.p2_support")}</strong>.
          </p>
        </div>
      </section>

      <section className="border-b hairline bg-[#fafafa]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">
          <div className="eyebrow mb-4">{t("about.promisesEyebrow")}</div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-10">{t("about.promisesH2")}</h2>
          <div className="space-y-4">
            {promises.map(([title, body], i) => (
              <div key={title} className="p-5 border hairline bg-white flex gap-4" data-testid={`promise-${i}`}>
                <Check className="w-5 h-5 text-[#ff4500] shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <div className="font-display text-lg mb-1">{title}</div>
                  <p className="text-sm text-[#555] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b hairline bg-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">
          <div className="eyebrow mb-4">{t("about.howEyebrow")}</div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-10">{t("about.howH2")}</h2>
          <div className="grid md:grid-cols-3 gap-0 border hairline">
            <How icon={MapPin} title={t("about.how_compare_t")} body={t("about.how_compare_b")} />
            <How icon={Users} title={t("about.how_talk_t")} body={t("about.how_talk_b")} border />
            <How icon={Sparkles} title={t("about.how_support_t")} body={t("about.how_support_b")} border />
          </div>
        </div>
      </section>

      <section className="bg-[#fafafa]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 text-center">
          <HeartHandshake className="w-10 h-10 text-[#ff4500] mx-auto mb-6" strokeWidth={1.25} />
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-4">
            {t("about.ctaH2")}
          </h2>
          <p className="text-[#555] mb-8 max-w-xl mx-auto">
            {t("about.ctaBody")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/community" className="btn-primary inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> {t("about.joinCampfire")}
            </Link>
            <Link to="/browse" className="btn-secondary inline-flex items-center gap-2">
              <Tent className="w-4 h-4" /> {t("about.browseCampsites")}
            </Link>
          </div>

          <div className="mt-12 pt-10 border-t hairline" data-testid="about-follow-cta">
            <div className="eyebrow text-[#ff4500] mb-3">{t("about.followEyebrow")}</div>
            <h3 className="font-display text-2xl lg:text-3xl mb-3">{t("about.followH3")}</h3>
            <p className="text-[15px] leading-relaxed text-[#444] max-w-xl mb-6">
              {t("about.followBody_pre")}{" "}
              <strong className="mono text-[#ff4500]">@camp.compassuk2026</strong>{" "}
              {t("about.followBody_mid")}{" "}
              <strong className="mono text-[#ff4500]">@campcompassuk2026</strong>{" "}
              {t("about.followBody_post")}
            </p>
            <SocialIcons size={22} showLabels testIdPrefix="about-social" className="!gap-6" />
          </div>
        </div>
      </section>

      <footer className="border-t hairline">
        <Footer />
      </footer>
    </div>
  );
}

const How = ({ icon: Icon, title, body, border }) => (
  <div className={`p-6 bg-white ${border ? "md:border-l hairline" : ""}`}>
    <Icon className="w-6 h-6 text-[#ff4500] mb-4" strokeWidth={1.5} />
    <h3 className="font-display text-lg mb-2">{title}</h3>
    <p className="text-sm text-[#555] leading-relaxed">{body}</p>
  </div>
);
